/**
 * Splits `openQuestionsMd` — a markdown bullet list reconcile writes and
 * never locks (Decision 2026-09-17) — into one question per bullet. Shared
 * by `OpenQuestionsCard` (across every capability on a property) and
 * `CapabilityDrawer`'s "Open questions" row (one capability), so the two
 * surfaces cannot drift on what counts as a bullet.
 *
 * Also exports `openQuestionsPrompt`, the one message both surfaces send
 * (via `askAssistant`, into a fresh thread) to open questions on.
 *
 * Only `-`/`*` bullets are recognized — the reconcile prompt's doctrine
 * constrains it to a flat bullet list, so anything else (a blank line, a
 * stray heading) is skipped rather than surfaced as a malformed question.
 *
 * @module features/product-map/openQuestions
 */

export function parseOpenQuestionBullets(openQuestionsMd: string | null): string[] {
  if (!openQuestionsMd) return [];
  return openQuestionsMd
    .split('\n')
    .map((line) => /^\s*[-*]\s+(.*\S)\s*$/.exec(line)?.[1])
    .filter((question): question is string => !!question);
}

export interface OpenQuestionLead {
  code: string | null;
  title: string;
  question: string;
}

/**
 * The message both the "Open questions" card and the capability drawer hand
 * to the assistant — sent (not pre-filled) into a fresh thread via
 * `askAssistant`, so the assistant starts working immediately rather than
 * waiting on the user to press send.
 *
 * An open question is a lead, not a dead end: the assistant is expected to
 * settle what it can itself — from the map, from the user's docs, or by
 * opening the app and looking — and only fall back to asking the user for
 * what genuinely can't be determined that way.
 *
 * With `lead`, the prompt points the assistant at a single question to start
 * with (the row/drawer this was opened from); without it, the assistant
 * starts from every open question on the map.
 */
export function openQuestionsPrompt(lead?: OpenQuestionLead): string {
  const base =
    "Work through the open questions on this application's map. Read the map first so you know what each capability is, then group the related questions. Answer the ones you can settle yourself — from the map, from my docs, or by opening the app and looking, showing me what you found — and ask me only the ones that genuinely can't be determined that way. When you ask, tell me which capability you mean and what it does, not just its code, and tell me what you already think the answer is. Write each answer into its capability (a Rule in the body or a Needs line in setup), remove that question, and keep going until none are left.";
  if (!lead) return base;
  return `${base} Start with ${lead.code ?? lead.title} "${lead.title}": "${lead.question}".`;
}
